import { keepPreviousData, skipToken, useQuery } from '@tanstack/react-query';
import { getV1CompaniesAccountDayJobStatsTypeahead } from '@treadinc/horizon-api-spec';
import axios from 'axios';
import { t } from 'i18next';

import { AccountCapacityTypeahead } from '~hooks/useAccount/models';
import { CapacityBreakdownRow } from '~hooks/useCapacityBreakdown/models';
import connection from '~services/connectionModule';
import { extractPagination, Pagination, PaginationLink } from '~services/pagination';

export const CAPACITY_BREAKDOWN_PAGE_SIZE = 50;

export const CAPACITY_BREAKDOWN_QUERY_KEY = 'capacityBreakdown';

const NO_ROWS: CapacityBreakdownRow[] = [];

interface UseCapacityBreakdownQueryProps {
  companyId: string;
  cursor?: string;
  equipmentTypeId?: string;
  link?: PaginationLink;
  search?: string;
}

export default function useCapacityBreakdownQuery({
  companyId,
  cursor,
  equipmentTypeId,
  link,
  search,
}: UseCapacityBreakdownQueryProps) {
  const query = useQuery({
    queryKey: [
      CAPACITY_BREAKDOWN_QUERY_KEY,
      companyId,
      equipmentTypeId,
      search,
      link,
      cursor,
    ],
    queryFn: companyId
      ? async ({ signal }) => {
          try {
            const readPages = async (
              startCursor?: string,
              startLink?: PaginationLink,
            ) => {
              const readDirection = startLink ?? 'after';
              let nextCursor = startCursor;
              let rows: CapacityBreakdownRow[] = [];
              let pagination: Pagination = {
                limit: CAPACITY_BREAKDOWN_PAGE_SIZE,
                page: 1,
              };

              // The endpoint pages by vendor and returns vendors with no committed types
              // too, so keep reading in the same direction until a page yields rows or the
              // vendors run out.
              do {
                const response = await getV1CompaniesAccountDayJobStatsTypeahead({
                  path: { 'company-id': companyId },
                  query: {
                    'page[limit]': CAPACITY_BREAKDOWN_PAGE_SIZE,
                    ...(nextCursor ? { [`page[${readDirection}]`]: nextCursor } : {}),
                    'filter[equipment_type_stats][equipment_type_id]': equipmentTypeId,
                    ...(search ? { 'search[query]': search } : {}),
                  },
                  signal,
                });

                rows = response.data.data.flatMap((stat) =>
                  CapacityBreakdownRow.fromVendor(
                    AccountCapacityTypeahead.parse(stat),
                    equipmentTypeId,
                  ),
                );
                pagination = extractPagination(response);
                nextCursor = pagination[readDirection];
              } while (rows.length === 0 && nextCursor);

              return { pagination, rows };
            };

            const requested = await readPages(cursor, link);
            const ranOutOfRows = requested.rows.length === 0 && link !== undefined;

            // Only empty vendors lay ahead in that direction, so show the first page
            // instead of an empty grid.
            if (ranOutOfRows) {
              const firstPage = await readPages();

              return { ...firstPage, isFirstPage: true };
            }

            const isFirstPage = link === undefined;

            // Every page skipped before the first populated one was empty, so nothing
            // precedes it.
            return {
              pagination: isFirstPage
                ? { ...requested.pagination, before: undefined }
                : requested.pagination,
              rows: requested.rows,
              isFirstPage,
            };
          } catch (error) {
            // React Query drops a read it cancelled, so there is nothing to report.
            if (axios.isCancel(error)) throw error;

            connection.handleRequestError(
              error,
              t('error_messages.account.failed_to_fetch_capacity_breakdown'),
            );
            throw error;
          }
        }
      : skipToken,
    // keeps the current page on screen, behind the loading state, while the next one loads
    placeholderData: keepPreviousData,
    // queryFn already toasts the failure, so retries would repeat it to the user.
    retry: false,
  });

  return {
    isError: query.isError,
    isFetching: query.isFetching,
    isFirstPage: query.data?.isFirstPage ?? true,
    pagination: query.data?.pagination,
    rows: query.data?.rows ?? NO_ROWS,
  };
}
